import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Coins, Loader2, Lock, Bitcoin, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { CreditPackBreakdown } from "@/components/CreditPackBreakdown";

// Lists admin-defined credit packs and starts a NOWPayments crypto checkout (redirect flow).
export function BuyCredits({ compact = false, bare = false, provider = "crypto", unavailable = false }) {
  const { user } = useAuth();
  const [busyId, setBusyId] = useState(null);

  const { data: packages, isLoading } = useQuery({
    queryKey: ["credit-packages"],
    queryFn: async () => (await api.get("/credit-packages")).data,
    staleTime: 0,
    refetchInterval: 30_000,
  });

  const buy = async (pkg) => {
    if (!user) { window.location.href = "/login"; return; }
    setBusyId(pkg.id);
    try {
      const res = await api.post("/payments/checkout", {
        package_id: pkg.id,
        provider,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url; // off to NOWPayments
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      toast.error(apiError(e, "Couldn't start checkout"));
      setBusyId(null);
    }
  };

  const list = packages || [];

  return (
    <div data-testid="buy-credits" className={bare ? "relative" : "hud-pill rounded-2xl p-5 sm:p-6 relative overflow-hidden"}>
      {!bare && <div className="absolute inset-0 holo-scan opacity-[0.06] pointer-events-none" />}
      {!bare && (
      <div className="flex items-center gap-3 mb-1">
        <div className="h-11 w-11 rounded-xl bg-amber-400/15 border border-amber-300/30 flex items-center justify-center">
          <Bitcoin className="h-6 w-6 text-amber-300" />
        </div>
        <div>
          <h2 className="font-display text-2xl tracking-[0.04em] text-white">BUY CREDITS</h2>
          <p className="text-white/50 text-sm">Top up with crypto — pay in BTC, ETH, USDT and more. Credits land the moment your payment confirms on-chain.</p>
        </div>
      </div>
      )}

      {/* Crypto-only purchase clarity */}
      {provider === "crypto" ? <div data-testid="crypto-notice" className="mt-4 rounded-xl border border-amber-300/25 bg-amber-300/[0.06] p-4 text-[12px] leading-relaxed text-white/70 space-y-1.5">
        <div className="flex items-center gap-2 text-amber-200 font-semibold text-[13px]"><Bitcoin className="h-4 w-4" /> Cryptocurrency checkout</div>
        <p>After you pay, it typically takes <span className="text-white font-semibold">15–20 minutes</span> for the transfer to move wallet-to-wallet — this is completely normal.</p>
        <p>Once the transaction clears, Playden updates <span className="text-emerald-300 font-semibold">automatically</span> and your credits are added — no need to refresh or contact anyone.</p>
        <p className="flex items-start gap-1.5 text-amber-200/90"><span className="mt-0.5">⚠️</span><span>If the payment arrives for less than the full amount, you'll receive <span className="font-semibold">partial credits</span> matching exactly what was received.</span></p>
      </div> : <div data-testid="stripe-notice" className="mt-4 flex items-center gap-2 text-sm text-emerald-200"><CreditCard className="h-4 w-4 shrink-0" /> {unavailable ? "Card checkout is currently unavailable." : "Secure card checkout with Stripe."}</div>}

      {isLoading ? (
        <div className="text-white/40 py-8 text-center text-sm">Loading packs…</div>
      ) : list.length === 0 ? (
        <div className="text-white/40 py-8 text-center text-sm">No credit packs available right now.</div>
      ) : (
        <div className="grid gap-3 mt-4 sm:grid-cols-2 xl:grid-cols-5">
          {list.map((p) => {
            const best = p.bonus_pct === p.bonus_cap_pct;
            const badge = p.bonus_label;
            return (
              <div
                key={p.id}
                data-testid={`credit-pack-${p.id}`}
                className={`relative rounded-xl border p-4 flex flex-col ${best ? "border-amber-300/50 bg-amber-300/[0.06]" : "border-white/10 bg-white/[0.03]"}`}
              >
                {badge ? (
                  <span data-testid={`credit-pack-bonus-${p.id}`} className={`absolute -top-2 left-3 text-[9px] font-mono uppercase tracking-[0.15em] px-2 py-0.5 rounded-full ${best ? "bg-amber-300 text-black" : "bg-emerald-400 text-black"}`}>
                    {badge}
                  </span>
                ) : null}
                <div data-testid={`credit-pack-name-${p.id}`} className="font-display text-lg text-white tracking-[0.04em]">{p.name}</div>
                <div className="flex items-center gap-1.5 mt-1 text-amber-300 font-mono">
                  <Coins className="h-4 w-4 shrink-0" /> <span data-testid={`credit-pack-total-${p.id}`} className="text-xl">{p.credits.toLocaleString()}</span>
                </div>
                <div className="font-mono text-[11px] text-white/45 mt-0.5">credits</div>
                <CreditPackBreakdown pack={p} prefix={`${provider}-pack-${p.id}`} />
                <Button
                  data-testid={`buy-credit-pack-${p.id}`}
                  onClick={() => buy(p)}
                  disabled={!!busyId || unavailable}
                  className={`mt-3 w-full font-medium ${best ? "bg-amber-300 text-black hover:bg-amber-200" : "bg-emerald-400 text-black hover:bg-emerald-300"}`}
                >
                  {busyId === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <>${p.price_usd.toFixed(2)}</>}
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <div data-testid="credit-checkout-provider" className="mt-4 flex items-center gap-2 text-[11px] text-white/50">
        <Lock className="h-3.5 w-3.5 text-amber-300/70 shrink-0" /> {provider === "stripe" ? "Stripe card processing · Powered by Steele Industries" : "Secure crypto checkout by NOWPayments. Non-custodial — we never touch your wallet."}
      </div>
    </div>
  );
}
